import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import useSWR from 'swr';

import { Notistack } from '@shared/components/Notistack/Notistack';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { queryParams } from '@shared/commonFunctions';
import { tranformPreferences, transformConsentData } from '@modules/ContactManager/components/Tabs/shared';
import { ConsentPreferences, ConsentPurposes, ConsentStatus } from '@modules/ContactManager/components/constants';
import { requestUpdateOneTrustOptIn, useGetConsentQuery } from '../../queries';
import { NContact } from '../../validation';
import {
  InitialOptOutStateType,
  IOptOutModalProps,
  IConsentPreferenceTypeItem,
  IConsentPreferencesDataResponse,
  IPurpose,
  ICustomPreference,
  IActiveDealCheckResponse,
  IUnsubscribeChecked,
} from './interfaces';
import { OptOutForm } from './OptOutForm';

export const OptOutModal = ({ handleClose, contactDetails, optOutModalPosition }: IOptOutModalProps) => {
  const params = queryParams();

  const [consentPreferencesTransformedData, setConsentPreferencesTransformedData] = useState<IPurpose[]>([]);
  const [consentPreferencesItems, setConsentPreferencesItems] = useState<IConsentPreferenceTypeItem[]>([]);

  const [unsubscribeChecked, setUnsubscribeChecked] = useState<IUnsubscribeChecked>({ isChecked: false, checkRender: 0 });

  const url = useMemo(() => {
    let baseUrl = `/${SERVICES.APICOMPOSER}/v1/contacts/${params.id}/notifications`;

    if (params.assetId) {
      baseUrl = `${baseUrl}?assetId=${params.assetId}`;
    }

    return baseUrl;
  }, [params.id, params.assetId]);

  const getConsentPath =
    contactDetails?.email && `${SERVICES.CONSENT}/consent/${contactDetails?.email}?firstName=${contactDetails?.firstName??''}&lastName=${contactDetails?.lastName??''}&phone=${contactDetails?.cellPhone??''}`;
  const { mutate: mutateErrorNotifications } = useSWR<NContact.Entity>(params.id && url);

  const {
    data: consentPreferencesData,
    isLoading: isConsentPreferencesDataLoading,
    isValidating: isConsentPreferencesDataValidating,
  } = useSWR<IConsentPreferencesDataResponse>(getConsentPath, {
      onError: (error) => {
        const errorType = error?.response?.data.errorType;
        const errorStatus = error?.response?.status;
        if (errorType === 'EntityNotFound' && errorStatus === 404) {
          return;
        }
        Notistack.generalError();
      }
  });
  
  const {
    data: activeDealsCheckData,
    isLoading: isActiveDealsCheckDataLoading,
    isValidating: isActiveDealsCheckDataValidating,
  } = useSWR<IActiveDealCheckResponse>(contactDetails?.email && `${SERVICES.DEAL}/contacts/${contactDetails?.email}/activedeal`, {
    onError: () => Notistack.generalError(),
  });

  const { mutate: reloadConsentPreferencesData } = useGetConsentQuery(contactDetails);

  useEffect(() => {
    if (!contactDetails?.email) {
      Notistack.generalError('This contact does not have personal email.');
    }
  }, [contactDetails?.email]);

  useEffect(() => {
    if (consentPreferencesData) {
      const transformedData = transformConsentData(consentPreferencesData?.purposes);
      setConsentPreferencesTransformedData(transformedData);
    }
  }, [consentPreferencesData]);

  useEffect(() => {
    if (consentPreferencesTransformedData.length > 0) {
      const transformedPurposes = tranformPreferences(consentPreferencesTransformedData);
      setConsentPreferencesItems(transformedPurposes);
    }
  }, [consentPreferencesTransformedData]);

  const initialConsentFormValues: InitialOptOutStateType = useMemo(
    () =>
      consentPreferencesItems.reduce((acc: InitialOptOutStateType, item: IConsentPreferenceTypeItem) => {
        acc[item.consentPreferenceName] = item.isOptedIn;

        return acc;
      }, {}) || {},
    [consentPreferencesItems]
  );

  const handleUnsubscribeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUnsubscribeChecked(({ checkRender }) => ({ isChecked: e.target.checked, checkRender: checkRender + 1 }));
      if (e.target.checked) {
        const resetFormValues = consentPreferencesItems.map((preference: IConsentPreferenceTypeItem) => ({
          ...preference,
          isOptedIn:
            (preference?.consentPreferenceName === ConsentPurposes.APPLICATION ||
              preference?.consentPreferenceName === ConsentPreferences.APPLICATION_CALL ||
              preference?.consentPreferenceName === ConsentPreferences.APPLICATION_EMAIL) &&
            activeDealsCheckData?.isActiveDealLead === true,
        }));
        setConsentPreferencesItems(resetFormValues);
      }
    },
    [consentPreferencesItems, activeDealsCheckData]
  );

  const handleUpdateReload = useCallback(() => {
    mutateErrorNotifications();
    reloadConsentPreferencesData();
    handleClose();
  }, []);

  const handleOptOutSubmit = useCallback(
    async (values: InitialOptOutStateType) => {
      const mappedPurposes = consentPreferencesTransformedData?.map((purpose: IPurpose) => {
        const isOptedIn = values[purpose?.name];
        const status = isOptedIn === true ? ConsentStatus.ACTIVE : ConsentStatus.WITHDRAWN;

        return {
          name: purpose?.name,
          status: status,
          customPreferences: purpose.customPreferences.map((pref: ICustomPreference) => ({
            name: pref?.name,
            options: pref?.options?.map((option) => {
              const isConsentedOption = values[`${purpose.name}_${option.name}`];
              return {
                name: option?.name,
                isConsented: isConsentedOption,
              };
            }),
          })),
        };
      });

      await requestUpdateOneTrustOptIn(contactDetails?.email?? '', mappedPurposes, handleUpdateReload);
    },
    [consentPreferencesTransformedData, contactDetails?.email]
  );

  return (
    <Modal
      open
      hideBackdrop
      title="Consent Preferences"
      dialogMaxWidth="620px"
      dialogMinWidth="600px"
      dialogHeight="575px"
      buttonText="Update"
      marginTop="45px"
      resetPosition={optOutModalPosition}
      onClose={handleClose}
    >
      <Formik
        key={unsubscribeChecked?.checkRender}
        enableReinitialize
        initialValues={initialConsentFormValues as InitialOptOutStateType}
        onSubmit={handleOptOutSubmit}
      >
        {(props) => (
          <Form>
            <OptOutForm
              {...props}
              items={consentPreferencesItems ?? []}
              isActiveDeal={activeDealsCheckData?.isActiveDealLead === true}
              handleUndoChanges={props.handleReset}
              handleUnsubscribeChange={handleUnsubscribeChange}
              unsubscribeChecked={unsubscribeChecked}
              setUnsubscribeChecked={setUnsubscribeChecked}
              isLoading={
                isConsentPreferencesDataLoading ||
                isConsentPreferencesDataValidating ||
                isActiveDealsCheckDataLoading ||
                isActiveDealsCheckDataValidating
              }
            />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
